import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  BreadcrumbGroup,
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  ExpandableSection,
  FormField,
  Header,
  Input,
  ProgressBar,
  SpaceBetween,
  StatusIndicator,
  Textarea,
} from '@cloudscape-design/components';
import {
  fetchWorkflow,
  updateStep,
  updateWorkflow,
  type WorkflowInstance,
  type StepDefinition,
  type StepProgress,
  type StepStatus,
} from '../api/onboarding-manager';

const STEP_STATUS: Record<StepStatus, { type: 'pending' | 'in-progress' | 'success' | 'stopped' | 'warning'; label: string }> = {
  pending: { type: 'pending', label: 'Pending' },
  in_progress: { type: 'in-progress', label: 'In Progress' },
  completed: { type: 'success', label: 'Completed' },
  skipped: { type: 'stopped', label: 'Skipped' },
  blocked: { type: 'warning', label: 'Blocked' },
};

const STEP_TYPE_ICON: Record<string, string> = {
  action: '⚡', checklist: '☑️', document: '📄', deploy: '🚀', wait: '⏳', review: '👁️', call: '📞',
};

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function OnboardingDetail() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get('tenantId') || '';
  const navigate = useNavigate();

  const [workflow, setWorkflow] = useState<WorkflowInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<number | null>(null);

  useEffect(() => {
    if (workflowId && tenantId) loadWorkflow();
  }, [workflowId, tenantId]);

  async function loadWorkflow() {
    setLoading(true);
    try {
      const wf = await fetchWorkflow(workflowId!, tenantId);
      setWorkflow(wf);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflow');
    } finally {
      setLoading(false);
    }
  }

  async function handleChecklistToggle(stepIndex: number, itemId: string, checked: boolean) {
    if (!workflow) return;
    setSaving(stepIndex);
    try {
      const result = await updateStep(workflowId!, tenantId, {
        stepIndex,
        checklistProgress: { [itemId]: checked },
      });
      setWorkflow((prev) => prev ? { ...prev, progress: result.progress, currentStep: result.currentStep, status: result.workflowStatus } : prev);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(null);
    }
  }

  async function handleDocumentLink(stepIndex: number, docId: string, link: string) {
    if (!workflow) return;
    setSaving(stepIndex);
    try {
      const result = await updateStep(workflowId!, tenantId, {
        stepIndex,
        documentLinks: { [docId]: link },
      });
      setWorkflow((prev) => prev ? { ...prev, progress: result.progress, currentStep: result.currentStep, status: result.workflowStatus } : prev);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(null);
    }
  }

  async function handleMarkComplete(stepIndex: number) {
    if (!workflow) return;
    setSaving(stepIndex);
    try {
      const result = await updateStep(workflowId!, tenantId, {
        stepIndex,
        status: 'completed',
      });
      setWorkflow((prev) => prev ? { ...prev, progress: result.progress, currentStep: result.currentStep, status: result.workflowStatus } : prev);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(null);
    }
  }

  async function handleSkipStep(stepIndex: number) {
    if (!workflow) return;
    setSaving(stepIndex);
    try {
      const result = await updateStep(workflowId!, tenantId, {
        stepIndex,
        status: 'skipped',
      });
      setWorkflow((prev) => prev ? { ...prev, progress: result.progress, currentStep: result.currentStep, status: result.workflowStatus } : prev);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setSaving(null);
    }
  }

  async function handleStepNotes(stepIndex: number, notes: string) {
    if (!workflow) return;
    try {
      const result = await updateStep(workflowId!, tenantId, { stepIndex, notes });
      setWorkflow((prev) => prev ? { ...prev, progress: result.progress } : prev);
    } catch { /* silent */ }
  }

  async function handleCancelWorkflow() {
    if (!workflow || !confirm('Cancel this onboarding workflow?')) return;
    try {
      await updateWorkflow(workflowId!, tenantId, { status: 'cancelled' });
      setWorkflow((prev) => prev ? { ...prev, status: 'cancelled' } : prev);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel');
    }
  }

  if (loading) return <Box textAlign="center" padding="xxl"><StatusIndicator type="loading">Loading workflow...</StatusIndicator></Box>;
  if (!workflow) return <Alert type="error">Workflow not found</Alert>;

  const completedSteps = workflow.progress.filter((p) => p.status === 'completed' || p.status === 'skipped').length;
  const percentComplete = Math.round((completedSteps / workflow.totalSteps) * 100);

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: 'Onboarding Manager', href: '/ops/onboarding' },
          { text: workflow.templateName, href: '#' },
        ]}
        onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }}
      />

      {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}

      <Header
        variant="h1"
        description={`${workflow.templateName} · ${workflow.productId}`}
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={loadWorkflow}>Refresh</Button>
            {workflow.status !== 'completed' && workflow.status !== 'cancelled' && (
              <Button onClick={handleCancelWorkflow}>Cancel Workflow</Button>
            )}
          </SpaceBetween>
        }
      >
        {workflow.customerContact?.name || workflow.tenantId.slice(0, 8)} — Onboarding
      </Header>

      {/* Overview */}
      <Container>
        <ColumnLayout columns={4} variant="text-grid">
          <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">Status</Box>
            <StatusIndicator type={workflow.status === 'completed' ? 'success' : workflow.status === 'cancelled' ? 'stopped' : 'in-progress'}>
              {workflow.status.replace('_', ' ')}
            </StatusIndicator>
          </SpaceBetween>
          <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">Progress</Box>
            <ProgressBar value={percentComplete} label={`${completedSteps}/${workflow.totalSteps}`} />
          </SpaceBetween>
          <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">Assigned To</Box>
            <Box>{workflow.assignedTo || '—'}</Box>
          </SpaceBetween>
          <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">Customer</Box>
            <Box>{workflow.customerContact ? `${workflow.customerContact.name} (${workflow.customerContact.email})` : '—'}</Box>
          </SpaceBetween>
        </ColumnLayout>
      </Container>

      {/* Steps */}
      {workflow.steps.map((step, idx) => (
        <StepCard
          key={idx}
          step={step}
          progress={workflow.progress[idx]!}
          isCurrent={idx === workflow.currentStep}
          saving={saving === idx}
          workflowDone={workflow.status === 'completed' || workflow.status === 'cancelled'}
          onChecklistToggle={(itemId, checked) => handleChecklistToggle(idx, itemId, checked)}
          onDocumentLink={(docId, link) => handleDocumentLink(idx, docId, link)}
          onMarkComplete={() => handleMarkComplete(idx)}
          onSkip={() => handleSkipStep(idx)}
          onNotesChange={(notes) => handleStepNotes(idx, notes)}
        />
      ))}
    </SpaceBetween>
  );
}

// ── Step Card Component ──

interface StepCardProps {
  step: StepDefinition;
  progress: StepProgress;
  isCurrent: boolean;
  saving: boolean;
  workflowDone: boolean;
  onChecklistToggle: (itemId: string, checked: boolean) => void;
  onDocumentLink: (docId: string, link: string) => void;
  onMarkComplete: () => void;
  onSkip: () => void;
  onNotesChange: (notes: string) => void;
}

function StepCard({ step, progress, isCurrent, saving, workflowDone, onChecklistToggle, onDocumentLink, onMarkComplete, onSkip, onNotesChange }: StepCardProps) {
  const s = STEP_STATUS[progress.status];
  const isDone = progress.status === 'completed' || progress.status === 'skipped';
  const isActive = progress.status === 'in_progress';
  const [notesValue, setNotesValue] = useState(progress.notes || '');
  const [docInputs, setDocInputs] = useState<Record<string, string>>(progress.documentLinks || {});

  return (
    <Container
      variant={isCurrent ? 'default' : 'stacked'}
      header={
        <Header
          variant="h3"
          description={step.description}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {saving && <StatusIndicator type="loading">Saving...</StatusIndicator>}
              <StatusIndicator type={s.type}>{s.label}</StatusIndicator>
              {step.estimatedMinutes && !isDone && (
                <Badge color="grey">~{step.estimatedMinutes < 60 ? `${step.estimatedMinutes}m` : `${Math.round(step.estimatedMinutes / 60)}h`}</Badge>
              )}
            </SpaceBetween>
          }
        >
          <SpaceBetween direction="horizontal" size="xs">
            <span>{STEP_TYPE_ICON[step.type] || '📋'}</span>
            <span>Step {step.index + 1}: {step.title}</span>
          </SpaceBetween>
        </Header>
      }
    >
      <SpaceBetween size="m">
        {/* Instructions */}
        {step.instructions && step.instructions.length > 0 && (
          <ExpandableSection headerText="Instructions" defaultExpanded={isActive}>
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              {step.instructions.map((inst, i) => (
                <li key={i} style={{ marginBottom: 4 }}>{inst}</li>
              ))}
            </ol>
          </ExpandableSection>
        )}

        {/* Checklist */}
        {step.type === 'checklist' && step.checklist && (
          <SpaceBetween size="xs">
            {step.checklist.map((item) => (
              <Checkbox
                key={item.id}
                checked={progress.checklistProgress?.[item.id] === true}
                onChange={({ detail }) => onChecklistToggle(item.id, detail.checked)}
                disabled={isDone || workflowDone}
                description={item.description}
              >
                {item.label} {item.required && <Badge color="red">Required</Badge>}
              </Checkbox>
            ))}
          </SpaceBetween>
        )}

        {/* Documents */}
        {step.type === 'document' && step.documents && (
          <SpaceBetween size="s">
            {step.documents.map((doc) => (
              <FormField
                key={doc.id}
                label={
                  <SpaceBetween direction="horizontal" size="xxs">
                    <span>{doc.label}</span>
                    {doc.required && <Badge color="red">Required</Badge>}
                    {progress.documentLinks?.[doc.id] && <Badge color="green">✓ Linked</Badge>}
                  </SpaceBetween>
                }
                description={doc.description}
              >
                <SpaceBetween direction="horizontal" size="xs">
                  <Input
                    value={docInputs[doc.id] || ''}
                    onChange={({ detail }) => setDocInputs((prev) => ({ ...prev, [doc.id]: detail.value }))}
                    placeholder="Paste document link..."
                    disabled={isDone || workflowDone}
                  />
                  <Button
                    onClick={() => onDocumentLink(doc.id, docInputs[doc.id] || '')}
                    disabled={isDone || workflowDone || !docInputs[doc.id]}
                  >
                    Save
                  </Button>
                </SpaceBetween>
              </FormField>
            ))}
          </SpaceBetween>
        )}

        {/* Wait hint */}
        {step.type === 'wait' && step.waitHint && (
          <Alert type="info">{step.waitHint}</Alert>
        )}

        {/* Call config */}
        {step.type === 'call' && step.callConfig && (
          <ColumnLayout columns={2} variant="text-grid">
            {step.callConfig.contactEmail && (
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Contact</Box>
                <Box>{step.callConfig.contactEmail}</Box>
              </SpaceBetween>
            )}
            {step.callConfig.meetingLink && (
              <SpaceBetween size="xxs">
                <Box variant="awsui-key-label">Meeting Link</Box>
                <Box><a href={step.callConfig.meetingLink} target="_blank" rel="noreferrer">{step.callConfig.meetingLink}</a></Box>
              </SpaceBetween>
            )}
          </ColumnLayout>
        )}

        {/* Notes */}
        {(isActive || isDone) && (
          <ExpandableSection headerText="Notes" defaultExpanded={false}>
            <SpaceBetween direction="horizontal" size="xs">
              <div style={{ flex: 1 }}>
                <Textarea
                  value={notesValue}
                  onChange={({ detail }) => setNotesValue(detail.value)}
                  rows={2}
                  placeholder="Add notes about this step..."
                  disabled={workflowDone}
                />
              </div>
              <Button onClick={() => onNotesChange(notesValue)} disabled={workflowDone}>Save</Button>
            </SpaceBetween>
          </ExpandableSection>
        )}

        {/* Timestamps */}
        {(progress.startedAt || progress.completedAt) && (
          <Box fontSize="body-s" color="text-status-inactive">
            {progress.startedAt && `Started: ${formatDate(progress.startedAt)}`}
            {progress.completedAt && ` · Completed: ${formatDate(progress.completedAt)}`}
          </Box>
        )}

        {/* Actions */}
        {isActive && !workflowDone && step.type !== 'checklist' && step.type !== 'document' && (
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={onSkip}>Skip</Button>
              <Button variant="primary" onClick={onMarkComplete} loading={saving}>
                Mark Complete
              </Button>
            </SpaceBetween>
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
}
